import { Button, Space, Typography } from 'antd';

import { StyledModal } from 'common/components';

import { useCheckPaymentTerm } from 'pages/home/ribbon/components/sections/product/components/cod-payment';

import { WizardDescription } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardDescription';

const { Text } = Typography;

const modalProps = {
  title: 'Confirm Submission',
  destroyOnClose: true,
  width: 600,
  maskClosable: false,
  zIndex: 1100,
};

export const WizardSubmissionConfirmation = ({
  visible,
  onCancel,
  onContinue,
  wizardPaymentInfo,
  isSubmitting,
}) => {
  const { isCODTerm, isInvoicedTerm } = useCheckPaymentTerm();

  const { unitCount = 0 } = wizardPaymentInfo ?? {};

  // Build the submission summary text
  const renderSubmissionSummary = () => {
    return (
      <WizardDescription style={{ marginBottom: 16 }}>
        You have submitted <Text strong>{unitCount}</Text>{' '}
        {unitCount === 1 ? 'product' : 'products'} to be professionally captured
        by <Text strong>IX-ONE</Text>.
      </WizardDescription>
    );
  };

  return (
    <StyledModal
      {...modalProps}
      visible={visible}
      onCancel={onCancel}
      footer={
        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type='primary' onClick={onContinue} loading={isSubmitting}>
            Continue
          </Button>
        </Space>
      }
    >
      {/* Submission Summary */}
      {renderSubmissionSummary()}

      {/* Payment Terms */}
      <WizardDescription
        style={{ marginBottom: 12, fontSize: 13, color: '#666' }}
      >
        {isInvoicedTerm && (
          <Text>You will be invoiced upon project capture completion.</Text>
        )}
        {isCODTerm && (
          <Text>
            Your credit card will be charged upon product receipt at{' '}
            <Text strong>IX-ONE Studios</Text>.
          </Text>
        )}
      </WizardDescription>

      <WizardDescription style={{ marginBottom: 0 }}>
        Please click <Text strong>[Continue]</Text> to proceed with the
        submission.
      </WizardDescription>
    </StyledModal>
  );
};

// Shared success message — reused by both WizardSubmissionSuccess (paid
// submissions) and WizardZeroCostSuccessModal (zero-cost submissions).
export const SubmissionSuccessContent = () => (
  <WizardDescription style={{ marginBottom: 4, lineHeight: 1.8 }}>
    Your submission has been successfully processed, and our professional
    capture studio is now on the lookout for your products to arrive. Shipping
    authorization has been sent to your email address.
  </WizardDescription>
);

export const WizardZeroCostSuccessModal = ({
  visible,
  onCancel,
  onCloseWizard,
}) => (
  <StyledModal
    {...modalProps}
    closable={false}
    title='Submission Successful'
    visible={visible}
    onCancel={onCancel}
    footer={
      <Space>
        <Button
          type='primary'
          onClick={() => {
            onCloseWizard();
            onCancel();
          }}
        >
          Close
        </Button>
      </Space>
    }
  >
    <SubmissionSuccessContent />
  </StyledModal>
);

export const WizardSubmissionSuccess = ({
  visible,
  onCancel,
  hasErrors = false,
  onReviewProducts,
  onCloseWizard,
  ...props
}) => {
  const renderErrorContent = () => (
    <>
      <WizardDescription style={{ fontWeight: 600 }}>
        No products have been submitted at this time.
      </WizardDescription>
      <WizardDescription>
        The following concerns have been found with the product data provided.
        Please review the highlighted information and update the information
        prior to submission.
      </WizardDescription>
      <WizardDescription>
        Conversely, you can choose to submit the products which passed
        validation, and resolve those items that are flagged at a later time.
      </WizardDescription>
    </>
  );

  const renderErrorFooter = () => (
    <Space>
      <Button onClick={onReviewProducts}>Review Products</Button>
    </Space>
  );

  const renderSuccessFooter = () => (
    <Space>
      <Button
        type='primary'
        onClick={() => {
          onCloseWizard();
          onCancel();
        }}
      >
        Close
      </Button>
    </Space>
  );

  return (
    <StyledModal
      {...modalProps}
      {...props}
      closable={false}
      title={hasErrors ? 'Submission Review Required' : 'Submission Successful'}
      visible={visible}
      onCancel={onCancel}
      footer={hasErrors ? renderErrorFooter() : renderSuccessFooter()}
    >
      {hasErrors ? renderErrorContent() : <SubmissionSuccessContent />}
    </StyledModal>
  );
};
